import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Save, Loader2, Eye, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ProducerTermsPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const canEdit = hasPermission('settings.edit');

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const res = await api.get('/settings/producer-terms');
      setTitle(res.data.title || '');
      setContent(res.data.content || '');
      setLastUpdated(res.data.last_updated || '');
    } catch (error) {
      console.error('Error fetching producer terms:', error);
      toast.error('Error al cargar términos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('No tenés permisos para editar');
      return;
    }

    setSaving(true);
    try {
      await api.put('/settings/producer-terms', { title, content });
      toast.success('Términos guardados correctamente');
      fetchTerms();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 lg:p-8 max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin/formularios" className="p-2 hover:bg-zinc-800 rounded-lg">
                <ArrowLeft size={20} className="text-zinc-400" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FileText className="text-[#D4AF37]" />
                  Términos para Productoras
                </h1>
                <p className="text-zinc-400 text-sm mt-1">
                  Estos términos se muestran a las productoras cuando crean eventos
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                className="border-zinc-700"
              >
                <Eye size={16} className="mr-2" />
                Vista previa
              </Button>
              {canEdit && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                  Guardar
                </Button>
              )}
            </div>
          </div>

          {lastUpdated && (
            <p className="text-sm text-zinc-500 mb-4">
              Última actualización: {lastUpdated}
            </p>
          )}

          {/* Editor */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-6">
            <div>
              <Label className="text-zinc-400">Título del documento</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Términos y Condiciones para Productoras"
                className="bg-zinc-800 border-zinc-700 mt-2"
                disabled={!canEdit}
              />
            </div>

            <div>
              <Label className="text-zinc-400">Contenido (soporta Markdown)</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="## 1. Aceptación de Términos..."
                className="bg-zinc-800 border-zinc-700 mt-2 min-h-[400px] font-mono text-sm"
                disabled={!canEdit}
              />
              <p className="text-xs text-zinc-500 mt-2">
                Usa ## para títulos, - para listas, **texto** para negrita
              </p>
            </div>
          </div>

          {/* Preview Modal */}
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle className="text-white">{title}</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[60vh] prose prose-invert prose-sm p-4">
                <div 
                  className="text-zinc-300 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ 
                    __html: content.replace(/\n/g, '<br/>') 
                  }} 
                />
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default ProducerTermsPage;
